import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { openFunnelBoard } from './funnelMove.functions.ts';
import { tFunnelMoveBubbleProps } from './FunnelMoveBubble.types.ts';
import './FunnelMoveBubble.styles.scss';

/**
 * Lilac in-chat card announcing that the Lead Agent moved this lead to a
 * different funnel (RET-1311). Mirrors the escalation NoteBubble treatment
 * (violet surface + SparklesIcon) and adds a one-click deep-link to the
 * destination funnel. Rendered from the client-synthesized 'funnel-move'
 * SYSTEM event, so it carries the structured funnel/stage identifiers the
 * deep-link needs — the durable persisted note is prose-only.
 */
export default function FunnelMoveBubble(props: tFunnelMoveBubbleProps) {
  const { event } = props;
  const { t } = useTranslation('chat/chat');
  const navigate = useNavigate();

  const funnelMove = event.system?.funnelMove;
  const funnelName = funnelMove?.toFunnel?.name;
  const stageName = funnelMove?.toStage?.name;
  const reason = funnelMove?.reason;
  const toFunnelUuid = funnelMove?.toFunnel?.uuid;

  function buildBody(): string {
    if (funnelName && stageName) {
      return t('smartLeadbot.systemFunnelMove', {
        funnel: funnelName,
        stage: stageName,
      });
    }
    if (funnelName) {
      return t('smartLeadbot.systemFunnelMoveNoStage', { funnel: funnelName });
    }
    // Partial payload with no funnel name — fall back to the pre-built text on
    // the synthesized event, then to a generic label, never crash.
    return event.content?.text || t('smartLeadbot.funnelMoveGeneric');
  }

  function handleOpenFunnel() {
    if (!toFunnelUuid) return;
    // Shared deep-link mechanism (see funnelMove.functions.ts) — also used by
    // NoteBubble's persisted funnel-move rendering.
    openFunnelBoard(navigate, toFunnelUuid);
  }

  return (
    <div className="FunnelMoveBubble">
      <div className="FunnelMoveBubble__card">
        <div className="FunnelMoveBubble__header">
          <SparklesIcon className="w-3.5 h-3.5 shrink-0" />
          <span>{t('smartLeadbot.funnelMoveTitle')}</span>
        </div>
        <p className="FunnelMoveBubble__body">{buildBody()}</p>
        {reason && (
          <p className="FunnelMoveBubble__reason">
            {t('smartLeadbot.funnelMoveReason', { reason })}
          </p>
        )}
        {toFunnelUuid && (
          <button
            type="button"
            className="FunnelMoveBubble__cta"
            onClick={handleOpenFunnel}
          >
            <span>{t('smartLeadbot.funnelMoveCta')}</span>
            <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 shrink-0" />
          </button>
        )}
      </div>
    </div>
  );
}
